<script lang="ts">
  import './game-board.less';
  import {
    playerInstance,
    enemyInstance,
    activePlayer,
    inactivePlayer,
    options,
    screens,
    glossary,
    peekedDie,
    pointedDie,
    dragItem,
  } from '../store';
  import type { Die as DieType } from './models/die.type';
  import HealthBar from './components/health-bar.svelte';
  import HistoryBar from './components/history-bar.svelte';
  import Particles from './components/particle.svelte';
  import Portrait from './components/portrait.svelte';
  import DiceTray from './dice-tray/dice-tray.svelte';
  import DieElement from './dice-tray/die.svelte';
  import Icon from './icon/icon.svelte';
  import { DieMaterial } from './models/die.type';
  import {
    isTechniquePossible,
    type Technique,
    fitPositions,
    techniqueKind,
    countTechniques,
    TechniqueKind,
    techniqueKinds,
    techniquesOnOffer,
    techniqueKindIcon,
  } from './models/techniques.type';
  import Hover from './components/hover.svelte';
  import Text from './components/text.svelte';
  import Draggable from './components/draggable.svelte';
  import Droppable from './components/droppable.svelte';
  import { openOptions } from './modals/modals';
  import { onMount, untrack } from 'svelte';
  import { continueTutorial, TutorialEvent } from './utils/tutorial';

  import { flip } from 'svelte/animate';
  import { send, receive } from '../animation';

  import { randomFloat, clamp, distance } from './utils/math';

  import Chrome from './components/chrome.svelte';
  import { alert } from './modals/modals';
  import type { DropEvent } from './utils/utils';
  import { playAudio, SoundType } from './components/audio.svelte';
  import { openGlossary } from './glossary/glossary';
  import { Screen } from './models/screen.type';
  import { BattleState } from './models/battle.type';
  import { BattleEngine, dealDice, type BattleData } from './battle/engine';
  import { diceInSlots, slotFilled, techniqueFull, type Slots } from './battle/slots';

  interface Props {
    data: BattleData;
  }

  let { data }: Props = $props();

  /*
    The battle itself is the engine: turns, dice, techniques, undo and the AI.
    This component draws it and passes the player's input on. What it draws
    comes through the engine's two stores -- `view` for the dice, turn and
    history, `techniques` for the list on offer -- because the engine changes
    players, dice and techniques in place and then says so.
  */
  // untrack: a board is built for one battle, and `data` is never swapped under it.
  const battle = untrack(
    () =>
      new BattleEngine(data, {
        rollers: () => dieElements,
        scatter: moveDie,
        rolled: (count) => playAudio(count <= 2 ? SoundType.DiceSingle : SoundType.DiceMultiple),
        stateChanged: (state) =>
          continueTutorial({ type: TutorialEvent.BattleStateChange, value: state }),
        techniqueUsed: () => continueTutorial({ type: TutorialEvent.TechniqueUsed, value: true }),
        ended: showOutcome,
      }),
  );
  const view = battle.view;
  const techniques = battle.techniques;

  /** The dice shown in each tray while it is the other side's turn. */
  const playerDie = dealDice($playerInstance, 'p');
  const enemyDie = dealDice($enemyInstance, 'e');

  let positions: Array<Array<number>> = $state([]);
  let dieElements: DieElement[] = [];
  let diceContainer: HTMLDivElement;

  function showOutcome(outcome: BattleState.Win | BattleState.Lose) {
    const won = outcome == BattleState.Win;
    alert({
      title: won ? 'Victory' : 'Defeat',
      text: won ? 'You have defeated the enemy' : 'You have been defeated by the enemy',
      buttonText: 'Continue',
    }).then(() => {
      screens.set(Screen.BattleOver, data);
    });
  }

  function onUpdatePlayers() {
    battle.updatePlayers();
    battle.checkDied();
  }
  const onUpdateDice = () => battle.updateDice();
  window.addEventListener('updateDice', onUpdateDice);
  window.addEventListener('updatePlayers', onUpdatePlayers);

  function dropDieOnBoard(dropData: DropEvent) {
    battle.unlockDie(dropData.item);

    let posX = dropData.position.x;
    let posY = dropData.position.y;

    if (diceContainer) {
      posX -= diceContainer.getBoundingClientRect().left;
      posY -= diceContainer.getBoundingClientRect().top;
    }

    positions[dropData.item.index][2] = posX;
    positions[dropData.item.index][3] = posY;

    return true;
  }

  /** A die the player has touched is squared up, as if they had picked it up to sort it. */
  function straighten(index: number) {
    if (positions[index]) positions[index][4] = 0;
  }

  /*
    Where each die sits on the board, as [x%, y%, x px, y px, tilt deg]. A function
    declaration, since the engine is built above and may call it at once.
  */
  let lastWidth = 0;
  let lastHeight = 0;
  function moveDie(count: number, reset: boolean = false) {
    if (!diceContainer || !(diceContainer.clientWidth || lastWidth)) {
      setTimeout(() => {
        moveDie(count, reset);
      }, 100);

      for (let i: number = 0; i < count; i++) {
        positions[i] = new Array(4).fill(randomFloat(10, 80));
      }
      return;
    }

    for (let i: number = 0; i < count; i++) {
      if (reset) {
        positions[i] = [randomFloat(10, 80), randomFloat(10, 80)];
      } else {
        positions[i][0] = positions[i][0] + randomFloat(-15, 15);
        positions[i][1] = positions[i][1] + randomFloat(-15, 15);

        // Bind to screen
        positions[i][0] = clamp(positions[i][0], 10, 80);
        positions[i][1] = clamp(positions[i][1], 10, 80);
      }

      positions[i][2] = positions[i][0];
      positions[i][3] = positions[i][1];
      positions[i][4] = randomFloat(-14, 14);

      if (lastWidth && lastHeight) {
        positions[i][2] = (positions[i][0] * lastWidth) / 100;
        positions[i][3] = (positions[i][1] * lastHeight) / 100;
      } else if (diceContainer && diceContainer.clientWidth && diceContainer.clientHeight) {
        lastWidth = diceContainer.clientWidth;
        lastHeight = diceContainer.clientHeight;
        positions[i][2] = (positions[i][0] * lastWidth) / 100;
        positions[i][3] = (positions[i][1] * lastHeight) / 100;
      }

      // test position against previous positions for distance
      for (let j = 0; j < i; j++) {
        let dist = distance(positions[i][0], positions[i][1], positions[j][0], positions[j][1]);
        if (dist < 20) {
          i--;
          continue;
        }
      }
    }
  }

  function resize() {
    lastWidth = diceContainer.clientWidth;
    lastHeight = diceContainer.clientHeight;
    moveDie($view.board.length);
  }
  onMount(() => {
    window.addEventListener('resize', resize);
    battle.start();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('updateDice', onUpdateDice);
      window.removeEventListener('updatePlayers', onUpdatePlayers);
      battle.destroy();
    };
  });

  /**
   * The die the player is pointing at -- dragging, hovering or holding -- whose
   * fit the technique list shows: its slots lit, techniques it cannot join
   * dimmed. Only the player's own dice, locked or already in a technique.
   */
  let focusDie: DieType | null = $derived.by(() => {
    if (!$view.playerActive) return null;
    const dragged = $dragItem?.data;
    const die: DieType | null =
      dragged && 'faces' in dragged ? dragged : ($pointedDie ?? $peekedDie ?? null);
    if (!die) return null;
    const inSlot = Object.values($view.slots).some((slots) => slots.includes(die));
    return $view.locked.includes(die) || inSlot ? die : null;
  });

  /** The slots of `technique` the focused die would go into; [] if none. */
  function focusSlots(technique: Technique) {
    if (!focusDie || !technique.possible) return [];
    return fitPositions(focusDie, technique, slotsOf(technique)) || [];
  }

  /** The die in each of a technique's slots. */
  function slotsOf(technique: Technique): Slots {
    return $view.slots[technique.title] ?? [];
  }

  /*
    A die that fills two slots is drawn as itself in the first and as a locked
    copy in the rest. The copies are kept, so each keeps its id -- the key its
    crossfade runs on -- from one redraw to the next.
  */
  // A cache, not state: nothing redraws from it changing.
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  const copies = new Map<string, DieType>();
  function slotDice(technique: Technique, index: number): Array<DieType> {
    const slots = slotsOf(technique);
    const die = slots[index];
    if (!die) return [];
    if (slots.indexOf(die) == index) return [die];

    const key = `${die.id}:${index}`;
    const kept = copies.get(key);
    const copy: DieType = kept && kept.type == die.type ? kept : die.clone();
    copy.id = `copy-${key}`;
    copy.material = DieMaterial.Cloned;
    copy.activeFace = die.activeFace;
    copies.set(key, copy);
    return [copy];
  }

  // Kinds the player has folded away. Grouping itself is an option.
  let collapsedKinds: Array<TechniqueKind> = $state([]);
  function toggleKind(kind: TechniqueKind) {
    collapsedKinds = collapsedKinds.includes(kind)
      ? collapsedKinds.filter((k) => k !== kind)
      : [...collapsedKinds, kind];
  }

  /** X of Y per kind for the list on offer: possible now, out of all listed. */
  let kindCounts = $derived(countTechniques($techniques, (t) => !!t.possible));

  /** Whether this row starts a new kind, and so carries the kind's header. */
  function startsKind(index: number) {
    if (!$options.groupTechniques) return false;
    const list = $techniques;
    return index == 0 || techniqueKind(list[index]) !== techniqueKind(list[index - 1]);
  }

  /**
   * X of Y per kind for the strip over the action bar. While rolling there is
   * no list, so it is worked out from the faces showing on every die, locked
   * or not; in the action phase it is the list's own counts.
   */
  let stripCounts = $derived.by(() => {
    if ($view.state != BattleState.Rolling) return kindCounts;
    const dice = [...$view.locked, ...$view.board];
    return countTechniques(
      techniquesOnOffer(dice, $activePlayer),
      (t) => !!isTechniquePossible([], dice, t),
    );
  });

  /** Opens the glossary on this kind of technique, narrowed to the player's dice. */
  function browseKind(kind: TechniqueKind) {
    const dice =
      $view.state == BattleState.Rolling ? [...$view.locked, ...$view.board] : battle.available;
    glossary.selected.set('techniques');
    glossary.searchDie.set(dice.map((die) => die.clone()));
    glossary.searchKind.set(kind);
    glossary.open.set(true);
  }

  // Flipping the option mid-battle regroups the list there and then.
  $effect(() => {
    const grouped = !!$options.groupTechniques;
    untrack(() => battle.regroup(grouped));
  });
</script>

<div class="layout">
  <div class="column player">
    <Droppable
      onDrop={(drop: DropEvent) => battle.dropDieOnTray(drop.item)}
      itemArray={$view.locked}
    >
      <div class="column player">
        <div class="tray wood">
          {#if playerDie.length}
            <DiceTray
              dice={$view.playerActive ? $view.locked : playerDie}
              clickEvent={$view.playerActive ? (die: DieType) => battle.clickDie(die) : () => {}}
            />
          {/if}

          <HealthBar data={$playerInstance} />

          <!-- <button class='btn' on:click={()=>{endGame(BattleState.Win);}}>
            Autowin
            </button>
            <button class='btn' on:click={()=>{endGame(BattleState.Lose);}}>
              Autolose
            </button> -->
        </div>

        <Portrait data={$playerInstance} />
      </div>
    </Droppable>
  </div>

  <div class="column board background {data.battle.environment}">
    <HistoryBar history={$view.history} />

    <div
      class="dice-container {$view.state == BattleState.Rolling ? '' : 'hidden'}"
      bind:this={diceContainer}
    >
      <Droppable
        onDrop={(item: DropEvent) => {
          return dropDieOnBoard(item);
        }}
        itemArray={$view.board}
      >
        {#each $view.board as die, index (die.id)}
          <Draggable
            left={positions[die.index][2]}
            top={positions[die.index][3]}
            onClick={$view.playerActive
              ? () => {
                  battle.lockDie(die);
                }
              : () => {}}
            onHold={() => {
              peekedDie.set(die);
            }}
            onHoldEnd={() => {
              peekedDie.set(null);
            }}
            data={die}
          >
            <div
              class="slot slot-{die.index} board-die"
              class:straight={!positions[die.index][4]}
              style="--tilt: {positions[die.index][4] ?? 0}deg"
              onpointerdown={() => straighten(die.index)}
              in:receive={{ key: die.id }}
              out:send={{ key: die.id }}
            >
              <DieElement
                {die}
                bind:this={dieElements[die.index]}
                hover={true}
                showInfo={true}
                allDice={$view.dealt}
              />
            </div>
          </Draggable>
        {/each}
      </Droppable>
    </div>

    {#if $view.state == BattleState.ActionPhase}
      <div class="skills">
        {#each $techniques as technique, index (technique.title)}
          <div animate:flip={{ duration: 400 }} role="button" tabindex="0">
            {#if startsKind(index)}
              {@const kind = techniqueKind(technique)}
              <button
                class="kind-header kind-{kind.toLowerCase()} {collapsedKinds.includes(kind)
                  ? 'collapsed'
                  : ''}"
                onclick={() => toggleKind(kind)}
                aria-expanded={!collapsedKinds.includes(kind)}
              >
                <span class="chevron">▾</span>
                <Icon
                  name={techniqueKindIcon[kind]}
                  colour="currentColor"
                  width="1.1em"
                  height="1.1em"
                  class="kind-icon"
                />
                <span class="kind">{kind}</span>
                <span class="count">{kindCounts[kind].possible}/{kindCounts[kind].total}</span>
              </button>
            {/if}
            <Droppable
              onDrop={(item: DropEvent) => {
                return battle.dropDieOnTechnique(item.item, technique);
              }}
              fillHeight={false}
              itemArray={diceInSlots(slotsOf(technique))}
            >
              <div
                class="technique wood kind-{techniqueKind(technique).toLowerCase()}
            {technique.possible ? 'valid' : 'invalid'}
            {techniqueFull(technique, slotsOf(technique)) ? 'active' : ''}
            {focusDie && !focusSlots(technique).length ? 'dim' : ''}
            {$options.groupTechniques && collapsedKinds.includes(techniqueKind(technique))
                  ? 'folded'
                  : ''}
          "
              >
                <div class="ingredients">
                  {#each technique.ingredients as text, index}
                    <div
                      class="ingredient
                        {slotFilled(slotsOf(technique), index) ? 'filled' : 'empty'}
                        {focusSlots(technique).includes(index) ? 'target' : ''}"
                      role="button"
                      tabindex="0"
                      title="Fill this recipe from your locked dice"
                      onmousedown={() => {
                        battle.selectIngredients(technique);
                      }}
                      onkeydown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          battle.selectIngredients(technique);
                        }
                      }}
                    >
                      <!-- The face that belongs in this slot, showing through
                           until a die covers it. -->
                      <Icon name={text} class="ghost" />

                      {#each slotDice(technique, index) as die (die.id)}
                        <Draggable
                          left={0}
                          top={0}
                          data={die}
                          freeMovement={false}
                          locked={die.material == DieMaterial.Cloned}
                        >
                          <div in:receive={{ key: die.id }} out:send={{ key: die.id }}>
                            <DieElement {die} die3D={false} hover={false} />
                          </div>
                        </Draggable>
                      {/each}
                    </div>
                  {/each}

                  {#if technique.requirements}
                    {#each technique.requirements as requirement, index}
                      {#if requirement.icon}
                        <div
                          class="requirement {requirement.test($activePlayer, $inactivePlayer)
                            ? 'passed'
                            : ''}"
                        >
                          <Icon name={requirement.icon} width="40px" height="40px" />
                          {#if requirement.iconBonus}
                            <Icon name={requirement.iconBonus} width="60px" height="60px" />
                          {/if}

                          <Hover>
                            <div class="hover-icon">
                              <Icon name={requirement.icon} width="40px" height="40px" />
                              {#if requirement.iconBonus}
                                <Icon name={requirement.iconBonus} width="60px" height="60px" />
                              {/if}
                            </div>
                            <br />

                            <p>{requirement.text}</p>
                          </Hover>
                        </div>
                      {/if}
                    {/each}
                  {/if}
                </div>
                <div class="description">
                  <div class="text">
                    <h2>
                      {technique.title}
                    </h2>
                    <Text data={technique.text} />
                  </div>

                  <!-- Swaps in over the description once the recipe is full,
                       which is what keeps the row this short. -->
                  <div class="activate">
                    <Chrome
                      click={() => {
                        battle.clickTechnique(technique);
                      }}
                      chroming="gold"
                      edge="round"
                      thickness="thick"
                      gloss={true}
                    >
                      <div class="action">Activate</div>
                    </Chrome>
                  </div>
                </div>
              </div>
            </Droppable>
          </div>
        {/each}
      </div>
    {/if}

    <!--
      One bar for both phases, so they cannot drift apart again: the two ends
      change with the phase, and options and the glossary are always between.
    -->
    {#if $view.state == BattleState.Rolling || $view.state == BattleState.ActionPhase}
      <div class="actions actions-3">
        <!-- What the dice make now, out of what they could. The grouped list
             carries these in its headers, so this shows while rolling, and in
             the action phase only when grouping is off. -->
        {#if $view.playerActive && ($view.state == BattleState.Rolling || !$options.groupTechniques)}
          <div class="technique-counts">
            {#each techniqueKinds as kind}
              {#if stripCounts[kind].total}
                <button
                  class="kind-{kind.toLowerCase()}"
                  onclick={() => browseKind(kind)}
                  title="See your {kind} techniques in the glossary"
                >
                  <Icon
                    name={techniqueKindIcon[kind]}
                    colour="currentColor"
                    width="1em"
                    height="1em"
                    class="kind-icon"
                  />
                  {kind}
                  <span class="tally">{stripCounts[kind].possible}/{stripCounts[kind].total}</span>
                </button>
              {/if}
            {/each}
          </div>
        {/if}
        {#if $view.state == BattleState.Rolling}
          <Chrome
            click={() => battle.reroll()}
            chroming="silver"
            edge="round"
            thickness="thick"
            gloss={true}
          >
            <div class="action reroll"><Icon name="reroll" /></div>
            <p class="count">{$view.rerollRemaining}</p>
          </Chrome>
        {:else}
          <Chrome
            click={() => battle.undo()}
            chroming="silver"
            edge="round"
            thickness="thick"
            gloss={true}
          >
            <div class="action undo"><Icon name="undo" /></div>
          </Chrome>
        {/if}

        <div class="menu">
          <Chrome
            click={() => {
              openOptions({});
            }}
            chroming="silver"
            edge="round"
            thickness="thin"
            gloss={true}
          >
            <div class="action small"><Icon name="options" /></div>
          </Chrome>
          <Chrome click={openGlossary} chroming="silver" edge="round" thickness="thin" gloss={true}>
            <div class="action small"><Icon name="glossary" /></div>
          </Chrome>
        </div>

        <!-- Gold, like Activate: the button that moves the game on. -->
        {#if $view.state == BattleState.Rolling}
          <Chrome
            click={() => battle.lockin()}
            chroming="gold"
            edge="round"
            thickness="thick"
            gloss={true}
          >
            <div class="action lockin"><Icon name="clash" /></div>
          </Chrome>
        {:else}
          <Chrome
            click={$view.playerActive ? () => battle.endTurn() : () => {}}
            chroming="gold"
            edge="round"
            thickness="thick"
            gloss={true}
          >
            <div class="action endturn"><Icon name="endturn" /></div>
          </Chrome>
        {/if}
      </div>
    {/if}
  </div>

  <div class="column enemy">
    <Portrait data={$enemyInstance} />

    <div class="tray wood">
      <HealthBar data={$enemyInstance} reverse={true} />

      {#if enemyDie.length}
        <DiceTray clickEvent={() => {}} dice={!$view.playerActive ? $view.locked : enemyDie} />
      {/if}
    </div>
  </div>
</div>

<Particles />
